import { Form, Formik } from "formik";
import * as yup from "yup";
import { COUNTRY_CODE } from "utils/constants/countries";
import { useEffect, useState } from "react";
import { validatePhone } from "helpers/validation-helpers/inputValidationHelpers";
import axios from "axios";
import { LoaderOverlay } from "components/common/loaders";
import { useKeycloak } from "@react-keycloak/web";
import { LogoutButton } from "pages/NotFound";
import { Navigate } from "react-router-dom";
import "./sign-up.css";
import { ReduxState } from "redux-models/reduxStateTypes";
import { useSelector } from "react-redux";
import validateJWT from "utils/validateJWT";
import { AuthBranding } from "components/common/branding";
import {
  AuthContainer,
  FormContainer,
  Tabs,
} from "components/common/container";
import { extractAxiosErrorMessage } from "utils/AxiosError";
import { ROUTES } from "utils/constants/routes-constants";
import { JwtValidationErrors } from "utils/JwtValidationErrors";

const apiBaseUrl = process.env.REACT_APP_URL_API;
export interface TokenParsed extends Keycloak.KeycloakInstance {
  email: string;
  given_name: string;
  family_name: string;
}

const ErrorMessage = ({ message }: { message?: string }) => {
  if (!message) return null;

  return (
    <div className="error" style={{ color: "#d00e17" }}>
      {message}
    </div>
  );
};

const SignUp = () => {
  const genericMessage = "This information is required.";
  const [termsAccepted, setTermsAccepted] = useState(false);
  const defaultCountry = COUNTRY_CODE.find((v) => v.country === "Australia");
  const defaultRegion =
    COUNTRY_CODE.find((v) => v.country === "Australia")?.code ?? "";
  const [region, setRegion] = useState(defaultRegion || "");
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const { initialized, keycloak } = useKeycloak();
  const keycloakToken = keycloak.tokenParsed as TokenParsed;
  const [isDisabled, setIsDisabled] = useState(false);
  let emailAddress = keycloakToken?.email || "";
  const currentUser = useSelector(
    (state: ReduxState) => state.auth.currentUserDetails
  );
  const [showPassword, setShowPassword] = useState(false);
  const [scotPacToken, setScotPacToken] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState({
    firstName: "",
    surname: "",
    emailAddress: "",
    mobile: "",
    countryCode: defaultCountry?.dialCode,
    password: "",
  });
  const [existingUser, setExistingUser] = useState<boolean | null>(null);
  const [isInvite, setIsInvite] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const { search } = window.location;
    const emailVerifiedToken = new URLSearchParams(search);
    const jwtToken = emailVerifiedToken.get("token") || "";
    const invalidText = "Invalid JWT token";

    if (jwtToken && jwtToken !== "Window") {
      const validatedToken = validateJWT(jwtToken);

      if (validatedToken) {
        if (validatedToken.isValidToken) {
          setInitialValues({
            ...initialValues,
            emailAddress: validatedToken.sub || "",
          });
          emailAddress = validatedToken.sub || "";
          setExistingUser(validatedToken.existingUser);
          setShowPassword(!validatedToken.existingUser);
          setIsInvite(validatedToken.isInvite);
          setScotPacToken(jwtToken);
          prefillUserDetails();
        } else {
          setIsLoading(false);

          switch (validatedToken.error) {
            case JwtValidationErrors.TokenExpired:
              setError(
                "Your invitation has expired, please request a new one from partnersupport@scotpac.com.au"
              );
              break;
            case JwtValidationErrors.InvalidToken:
              setError(validatedToken.error ?? invalidText);
              break;
            default:
              setError(validatedToken.error ?? invalidText);
              break;
          }
        }
      } else {
        setError(invalidText);
        setIsLoading(false);
      }
    } else {
      setError("Missing token");
      setIsLoading(false);
    }
  }, [window.location.search, initialized, emailAddress]);

  const prefillUserDetails = () => {
    if (emailAddress && scotPacToken) {
      setIsLoading(true);
      axios
        .get(`${apiBaseUrl}/v1/account/prefill`, {
          params: { email: emailAddress },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${scotPacToken}`,
          },
        })
        .then((response) => {
          setInitialValues({
            ...initialValues,
            firstName:
              response.data.firstName ?? keycloakToken?.given_name ?? "",
            surname: response.data.lastName ?? keycloakToken?.family_name ?? "",
            mobile: response.data.mobile ?? "",
            emailAddress: emailAddress,
          });
        })
        .catch((error) => {
          console.log(error);
          setError("A problem occurred while fetching user details");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    if (initialized) setIsLoading(false);
    setIsDisabled(isInvite);
  };

  const validationSchema = () => {
    return yup.object().shape({
      firstName: yup.string().required(genericMessage),
      surname: yup.string().required(genericMessage),
      countryCode: yup.string().required(genericMessage),
      mobile: yup
        .string()
        .required(genericMessage)
        .test(
          "is-valid-phone",
          "Please enter a valid phone number.",
          function (value) {
            return validatePhone(value ?? "", region);
          }
        ),
      emailAddress: yup
        .string()
        .email("Please enter a valid email address.")
        .required(genericMessage),
    });
  };

  const inviteSignUpSubmit = (payload: any) => {
    axios
      .post(`${apiBaseUrl}/v1/register-invitation`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${scotPacToken}`,
        },
      })
      .then(() => {
        keycloak.login({
          loginHint: payload.email,
          redirectUri: `${window.location.origin}${ROUTES.HOME}`,
        });
      })
      .catch((error) => {
        setError(
          extractAxiosErrorMessage(
            error,
            "An error occurred while signing up. Please try again later."
          )
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const signUpSubmit = (payload: any) => {
    axios
      .post(`${apiBaseUrl}/v1/sign-up`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${scotPacToken}`,
        },
      })
      .then(() => {
        keycloak.login({
          loginHint: payload.email,
          redirectUri: `${window.location.origin}${ROUTES.HOME}`,
        });
      })
      .catch((error) => {
        setError(
          extractAxiosErrorMessage(
            error,
            "An error occurred while signing up. Please try again later."
          )
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  if (!currentUser && keycloak.tokenParsed) return <Navigate to="/" />;

  return (
    <div className="sign-up-container">
      {keycloak.authenticated && (
        <div style={{ position: "absolute", zIndex: "1" }}>
          <LogoutButton to="/logout">Logout</LogoutButton>
        </div>
      )}
      {isLoading && <LoaderOverlay />}
      <AuthBranding />
      <AuthContainer>
        <Tabs
          tabs={[
            {
              name: "createUserProfile",
              text: "1. Create User Profile",
              isActive: true,
            },
            {
              name: "createCompanyProfile",
              text: "2. Create Company Profile",
              isActive: false,
            },
          ]}
        />
        <FormContainer>
          <div className="login-pf-page">
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={validationSchema()}
              validate={(values) => {
                const errors: { [key: string]: string } = {};
                if (
                  showPassword &&
                  (!values.password || values.password.trim() === "")
                ) {
                  errors.password = "Password is required.";
                }

                return errors;
              }}
              onSubmit={(values, { setSubmitting }) => {
                const countryCode = values.countryCode ?? "";
                let mobileCopy = values.mobile;
                const mobileWithoutPlus = mobileCopy.replace(/^\+/, "");
                const countryCodeWithoutPlus = countryCode.replace(/^\+/, "");

                if (!mobileWithoutPlus.startsWith(countryCodeWithoutPlus)) {
                  mobileCopy = `${values.countryCode}${mobileCopy}`;
                }

                const payload = {
                  firstName: values.firstName,
                  lastName: values.surname,
                  email: values.emailAddress,
                  mobile: mobileCopy,
                  companyId: currentUser?.companyId || null,
                  password: values.password,
                };

                setIsLoading(true);

                if (!isInvite) {
                  signUpSubmit(payload);
                } else {
                  inviteSignUpSubmit(payload);
                }

                setSubmitting(false);
              }}
            >
              {({
                handleChange,
                handleBlur,
                values,
                errors,
                touched,
                isSubmitting,
                handleSubmit,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <ErrorMessage message={error} />
                  <div id="attributeList" className="attr">
                    <ul>
                      <li className="TextBox">
                        <div className="attrEntry">
                          <label htmlFor="firstName">Given Name</label>
                          <input
                            className={`textInput ${
                              errors.firstName && touched.firstName
                                ? "invalid"
                                : ""
                            }`}
                            placeholder="Given Name"
                            id="firstName"
                            name="firstName"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.firstName || initialValues.firstName}
                          />
                          {touched.firstName && (
                            <ErrorMessage message={errors.firstName} />
                          )}
                        </div>
                      </li>
                      <li className="TextBox">
                        <div className="attrEntry">
                          <label htmlFor="surname">Surname</label>
                          <input
                            className={`textInput ${
                              errors.surname && touched.surname ? "invalid" : ""
                            }`}
                            placeholder="Surname"
                            id="surname"
                            name="surname"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.surname || initialValues.surname}
                          />
                          {touched.surname && (
                            <ErrorMessage message={errors.surname} />
                          )}
                        </div>
                      </li>

                      <li className="DropdownSingleSelect">
                        <div className="attrEntry">
                          <label htmlFor="countryCode">Country</label>
                          <select
                            id="countryCode"
                            name="countryCode"
                            onChange={(e) => {
                              const selected = COUNTRY_CODE.find(
                                (country) => country.dialCode === e.target.value
                              );

                              setRegion(selected?.code || defaultRegion);
                              values.mobile = "";
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            value={values.countryCode || defaultRegion}
                          >
                            {COUNTRY_CODE.map((country, index) => (
                              <option
                                key={index}
                                value={country.dialCode || country.country}
                              >
                                {country.country} ({country.dialCode})
                              </option>
                            ))}
                          </select>
                          <ErrorMessage message={errors.countryCode} />
                        </div>
                      </li>
                      <li className="TextBox">
                        <div className="attrEntry">
                          <label htmlFor="mobile">Mobile Number</label>
                          <input
                            className={`textInput ${
                              errors.mobile && touched.mobile ? "invalid" : ""
                            }`}
                            placeholder="Mobile Number"
                            id="mobile"
                            name="mobile"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.mobile || initialValues.mobile}
                          />
                          {touched.mobile && (
                            <ErrorMessage message={errors.mobile} />
                          )}
                        </div>
                      </li>
                      <li className="TextBox">
                        <div className="attrEntry">
                          <label htmlFor="emailAddress">Email Address</label>
                          <input
                            disabled={
                              isDisabled ||
                              showPassword ||
                              existingUser === true
                            }
                            className={`textInput ${
                              errors.emailAddress && touched.emailAddress
                                ? "invalid"
                                : ""
                            }`}
                            placeholder="Email Address"
                            id="emailAddress"
                            name="emailAddress"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={
                              values.emailAddress || initialValues.emailAddress
                            }
                          />
                          {touched.emailAddress && (
                            <ErrorMessage message={errors.emailAddress} />
                          )}
                        </div>
                      </li>
                      <li className="Password" hidden={!showPassword}>
                        <div className="attrEntry">
                          <label htmlFor="password">Password</label>
                          <input
                            className={`textInput ${
                              errors.password && touched.password
                                ? "invalid"
                                : ""
                            }`}
                            placeholder="Password"
                            id="password"
                            name="password"
                            type="password"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password || ""}
                          />
                          {touched.password && (
                            <ErrorMessage message={errors.password} />
                          )}
                        </div>
                      </li>

                      <div>
                        <div
                          id="privacy"
                          className="privacy-check-container"
                          style={{ display: "flex" }}
                        >
                          <input
                            type="checkbox"
                            id="privacy-check"
                            name="privacy-check"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                          />
                          <label
                            htmlFor="privacy-check"
                            id="privacy-checkbox"
                          ></label>
                          <p>
                            I accept the ScotPac Partner Portal{" "}
                            <a
                              id="modal-btn"
                              className="terms-link"
                              href="https://www.scotpac.com.au/partner-portal-terms-and-conditions/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Terms and Conditions of Use
                            </a>
                          </p>
                        </div>
                      </div>
                    </ul>
                  </div>

                  <div className="buttons">
                    <button
                      id="continue"
                      disabled={
                        !termsAccepted ||
                        isSubmitting ||
                        Object.keys(errors).length > 0 ||
                        error !== ""
                      }
                      type="submit"
                    >
                      Create
                    </button>
                  </div>
                  {(showPassword || existingUser) && (
                    <div className="continue-message">
                      After creating your profile, you will be redirected to the
                      login page. Once logged in, please complete your company
                      profile setup to access all features.
                    </div>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </FormContainer>
        {!isInvite ? (
          <div className="sign-up-link-container">
            <p>
              Already have an account?
              <a id="sign-in" href="/">
                {" "}
                Sign in here
              </a>
            </p>
          </div>
        ) : null}
      </AuthContainer>
    </div>
  );
};

export default SignUp;
